const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const two = (n: number) => String(n).padStart(2, "0");
const hhmm = (d: Date) => `${two(d.getHours())}:${two(d.getMinutes())}`;
const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** `/Users/ana/projects/app` -> `~/projects/app` (the phone does not know the computer's home folder, so it guesses from common layouts). */
export function shortenPath(cwd: string): string {
  const m = /^(?:\/Users\/[^/]+|\/home\/[^/]+|\/root|[A-Za-z]:\\Users\\[^\\]+)(.*)$/.exec(cwd);
  if (!m) return cwd;
  const rest = m[1]!.replace(/\\/g, "/");
  return "~" + rest;
}

/**
 * An absolute path's root (a drive letter like `C:\`, a UNC share like `\\server\share\`, or `/`) and its named
 * segments, for building a breadcrumb trail that works for both Unix and Windows paths (the computer the phone talks
 * to may be either, regardless of what the phone itself is).
 */
export function splitAbsolutePath(path: string): { root: string; sep: "/" | "\\"; parts: string[] } {
  const drive = /^[A-Za-z]:[\\/]/.exec(path);
  if (drive) return { root: path.slice(0, 2) + "\\", sep: "\\", parts: path.slice(drive[0].length).split(/[\\/]/).filter(Boolean) };
  const unc = /^\\\\[^\\/]+[\\/][^\\/]+/.exec(path);
  if (unc) return { root: unc[0] + "\\", sep: "\\", parts: path.slice(unc[0].length).split(/[\\/]/).filter(Boolean) };
  return { root: "/", sep: "/", parts: path.split("/").filter(Boolean) };
}

/** The inverse of `splitAbsolutePath`: a root and segments back into one path, with the right separator. */
export function joinPath(root: string, sep: "/" | "\\", parts: string[]): string {
  if (sep === "\\") return parts.length ? root.replace(/\\$/, "") + "\\" + parts.join("\\") : root;
  return parts.length ? "/" + parts.join("/") : "/";
}

/** A folder path short enough for a list row: when it is long, its last two folders ("…/proj/my_app"), since the end is what tells folders apart. */
export function tailPath(path: string, max = 26): string {
  if (path.length <= max) return path;
  const parts = path.split(/[\\/]/).filter(Boolean);
  const tail = parts.slice(-2).join("/");
  if (tail.length + 2 <= max) return `…/${tail}`;
  const last = parts[parts.length - 1] ?? path;
  return last.length + 2 <= max ? `…/${last}` : `…${last.slice(-(max - 1))}`;
}

/** How long ago, compactly: "just now", "32 min", "2 h", "3 d". */
export function elapsed(ts: number, now = Date.now()): string {
  const min = Math.floor(Math.max(0, now - ts) / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h`;
  return `${Math.floor(h / 24)} d`;
}

/** What a command line starts, by its program's name ("/usr/bin/codex --x" is Codex). Anything else is shown as typed. */
export function agentOf(command: string): { key: "claude" | "codex" | "antigravity" | "other"; label: string } {
  const program = command.trim().split(/\s+/)[0] ?? "";
  const name = (program.split(/[\\/]/).pop() ?? "").toLowerCase().replace(/\.(exe|cmd|bat)$/, "");
  if (name === "claude") return { key: "claude", label: "Claude Code" };
  if (name === "codex") return { key: "codex", label: "Codex" };
  if (name === "agy" || name === "antigravity") return { key: "antigravity", label: "Antigravity" };
  return { key: "other", label: name || command };
}

/** "Today 09:12", "Yesterday 18:40", "Oct 5, 14:22", "Oct 5, 2025". */
export function sessionTime(ts: number, now = Date.now()): string {
  const d = new Date(ts);
  const n = new Date(now);
  if (sameDay(d, n)) return `Today ${hhmm(d)}`;
  const y = new Date(n);
  y.setDate(n.getDate() - 1);
  if (sameDay(d, y)) return `Yesterday ${hhmm(d)}`;
  if (d.getFullYear() === n.getFullYear()) return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${hhmm(d)}`;
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/** "Paired Oct 7, 2026". */
export function pairedDate(ts: number): string {
  const d = new Date(ts);
  return `Paired ${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/** "3f9a 1c2e 7b04 d19e". */
export function groupFingerprint(fp: string): string {
  return fp.match(/.{1,4}/g)?.join(" ") ?? fp;
}

/** Countdown for a pairing code: "in 4:32" or "Expired". */
export function expiresIn(expiresAtSec: number, nowMs = Date.now()): string {
  const left = Math.max(0, Math.ceil(expiresAtSec - nowMs / 1000));
  return left > 0 ? `in ${Math.floor(left / 60)}:${two(left % 60)}` : "Expired";
}

/** Host part of a relay URL, for display. */
export function relayHost(relayUrl: string): string {
  try {
    return new URL(relayUrl).host;
  } catch {
    return relayUrl;
  }
}

/** The command line typed in "New session", split on whitespace: `python3 -m http.server` -> command + args. No quote handling. */
export function splitCommand(line: string): { command: string; args: string[] } | null {
  const parts = line.trim().split(/\s+/).filter(Boolean);
  const [command, ...args] = parts;
  return command ? { command, args } : null;
}
